import { FormikErrors } from 'formik'
import { isEqual } from 'lodash'
import { useEffect, useState } from 'react'
import {
  calculateProductPriceRaw,
  formatCurrency,
  parseCurrencyToNumber,
  transformVariants,
} from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import { useGetVariantsQuery } from '../../redux-toolkits/products/products.slice'
import { FulfilmentCentre } from '../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import {
  Combination,
  CreateProductValues,
  CreatedProductVariantOption,
  Product,
  SingleVariant,
  TransformedVariant,
  VariantCombination,
} from '../../redux-toolkits/products/products.type'
import { Button } from '../button'
import Icon from '../icon'
import { Checkbox } from '../inputs/Checkbox'
import Dropdown from '../inputs/dropdown'
import { AddProductVariantModal } from '../modal/app_modals/addVariantModal'
import { EditVariantionCombinationModal } from '../modal/app_modals/editVariationModal'
import { UpdateItemStockModal } from '../modal/app_modals/updateItemStockmodal'
import { TableComponent } from '../table'
import { HeaderSubheader } from '../typography/HeaderSubheader'

export const ManageProductVariants = ({
  values,
  setFieldValue,
  productDetails,
  usesFulfilmentCentreInventory,
  fulfilmentCentres,
  onInventoryAdjusted,
}: {
  values: CreateProductValues
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => Promise<void> | Promise<FormikErrors<CreateProductValues>>
  productDetails?: Product
  usesFulfilmentCentreInventory: boolean
  fulfilmentCentres: FulfilmentCentre[]
  onInventoryAdjusted: (adjustment: {
    fulfilmentCentreId?: string
    variantCombinationId?: string
    quantityDelta: number
  }) => void
}) => {
  const [selectedVariant, setSelectedVariant] = useState<{
    index: number
    item: SingleVariant
  }>()
  const [selectedCombination, setSelectedCombination] = useState<{
    index: number
    item: VariantCombination
  }>()
  const [transformedVariants, setTransformedVariants] = useState<
    TransformedVariant[]
  >([])
  const [stockCombination, setStockCombination] = useState<VariantCombination>()

  const { handleModalOpen, setModalOpen, modal, modalOpen, handleModalClose } =
    useModalControl()

  const { isLoading: loadingVariants, data: variants } = useGetVariantsQuery({
    skip: '0',
    page: '1',
    limit: '100',
  })

  useEffect(() => {
    // Transform list of duplicate variants and values into list of single variant and array of all its values
    setTransformedVariants(transformVariants(values.variants))
  }, [values.variants])

  useEffect(() => {
    if (transformedVariants.length > 0) {
      // Get all variant combinations from the variants
      const variantCombinations = transformedVariants.reduce(
        (acc, values) =>
          acc.flatMap((accVal) =>
            values.values.map((value) => [
              ...accVal,
              {
                variant: values.variant,
                value: value,
              },
            ]),
          ),
        [[]] as Combination[][], // An array of array of variants that make up a single combination
      )

      // Create new combination array, reuse existing values for existing combinations
      const newCombinations = [
        ...variantCombinations.map((combo) => {
          const existingCombination = values.variantCombinations.find((com) => {
            const combinationVariantIds = com.variantCombination.map(
              (vc) => vc.variant?._id,
            )
            const combinationVariantValues = com.variantCombination.map(
              (vc) => vc.value,
            )

            const computedCombinationVariantIds = combo.map(
              (vc) => vc.variant?._id,
            )
            const computedCombinationVariantValues = combo.map((vc) => vc.value)

            return (
              isEqual(
                combinationVariantIds.sort(),
                computedCombinationVariantIds.sort(),
              ) &&
              isEqual(
                combinationVariantValues.sort(),
                computedCombinationVariantValues.sort(),
              )
            )
          })

          return {
            _id: existingCombination?._id,
            variantCombination: combo,
            inventoryCount: existingCombination?.inventoryCount ?? 0,
            fulfilmentCentreInventory:
              existingCombination?.fulfilmentCentreInventory,
            price: existingCombination?.price ?? 0,
            isActive: existingCombination?.isActive ?? true,
            images: existingCombination?.images ?? [],
          }
        }),
      ]

      // Set new combinations
      setFieldValue('variantCombinations', newCombinations)
    }
  }, [transformedVariants, productDetails])

  return (
    <div className="flex flex-col gap-6">
      <HeaderSubheader
        title="Product Variants"
        text="Specify product variants"
      />

      <Checkbox
        checkStatus={values.isVariant}
        text="This product has variants"
        handleChange={(status) => {
          setFieldValue('isVariant', status)
        }}
      />

      {values.isVariant && (
        <>
          <div className="flex items-center gap-4 justify-between">
            <p className="text-lg font-medium">Options</p>

            <Button
              type="button"
              label="Add option"
              onClick={() => {
                handleModalOpen('addProductVariantModal')
              }}
              className="!w-fit !px-0 !py-0 !gap-2"
              variant="plain"
              icon="plus-circle"
              iconPosition="right"
              iconSize={20}
            />
          </div>

          <div className="w-full overflow-x-scroll">
            <TableComponent
              headers={['Option name', 'Properties', 'Actions']}
              rows={transformedVariants.map((variant, idx) => {
                const key = `${idx}`

                return {
                  id: key,
                  content: [
                    variant.variant?.name ?? 'N/A',
                    variant.values.join(', '),
                    <Dropdown
                      key={`${key}-controls`}
                      menuButton={
                        <Icon
                          id="ellipses"
                          height={18}
                          width={18}
                          className=""
                        />
                      }
                      menuItems={[
                        {
                          name: 'Edit',
                          value: 'edit',
                          onClick: () => {
                            setSelectedVariant({
                              index: idx,
                              item: variant.variant,
                            })
                            handleModalOpen('addProductVariantModal')
                          },
                        },
                        {
                          name: 'Delete',
                          value: 'delete',
                          onClick: () => {
                            const variantsCopy: CreatedProductVariantOption[] =
                              JSON.parse(JSON.stringify(values.variants))
                            // Remove all occurrences of variants where id matches

                            const newVariants = variantsCopy.filter(
                              (variantC) =>
                                variantC.variant?._id !== variant.variant?._id,
                            )

                            setFieldValue('variants', newVariants)

                            if (newVariants.length === 0) {
                              setFieldValue('variantCombinations', [])
                            }
                          },
                        },
                      ]}
                    />,
                  ],
                }
              })}
              isEmpty={transformedVariants.length === 0}
              emptyText={
                <>
                  No variant options added yet.
                  <br />
                  Click ‘Add Option’ to create one for this product.
                </>
              }
            />
          </div>

          {values.variantCombinations.length > 0 && (
            <>
              <p className="text-lg font-medium">Variations</p>

              <div className="w-full overflow-x-scroll">
                <TableComponent
                  headers={[
                    'Variation name',
                    'Price',
                    'Total Price',
                    'Quantity in stock',
                    'Active',
                    'Actions',
                  ]}
                  rows={values.variantCombinations.map((combination, idx) => {
                    const key = `${idx}`

                    const combinationVariantPrices =
                      combination.variantCombination.map((item) => {
                        // Find price of variant
                        const matchingVariant = values.variants.find(
                          (v) =>
                            v.variant?._id === item.variant?._id &&
                            v.value === item.value,
                        )

                        const price = Number(matchingVariant?.price ?? 0)
                        const markUp = Number(matchingVariant?.markupValue ?? 0)

                        return price + price * (markUp / 100)
                      })

                    const totalCombinationPrice =
                      combinationVariantPrices.reduce(
                        (accumulator, currentValue) =>
                          accumulator + currentValue,
                        0,
                      )

                    const totalPrice =
                      totalCombinationPrice +
                      (parseCurrencyToNumber(
                        calculateProductPriceRaw(
                          parseCurrencyToNumber(values.price),
                          values.comboItems.map(({ ...others }) => {
                            return { ...others }
                          }),
                          values.isMarkupEnabled ?? false,
                          Number(values.markupValue) ?? 0,
                        ),
                      ) || 0)

                    return {
                      id: key,
                      content: [
                        combination.variantCombination
                          .map((item) => item.value)
                          .join(', '),
                        formatCurrency(totalCombinationPrice),
                        formatCurrency(totalPrice),
                        usesFulfilmentCentreInventory
                          ? 'Managed by centre'
                          : combination.inventoryCount,
                        <Checkbox
                          checkStatus={combination.isActive}
                          text=""
                          handleChange={(status) => {
                            const variantCombinationsCopy: VariantCombination[] =
                              JSON.parse(
                                JSON.stringify(
                                  values.variantCombinations ?? [],
                                ),
                              )

                            variantCombinationsCopy[idx] = {
                              ...variantCombinationsCopy[idx],
                              isActive: status,
                            }

                            setFieldValue(
                              'variantCombinations',
                              variantCombinationsCopy,
                            )
                          }}
                          key={`${key}-check`}
                        />,
                        <Dropdown
                          key={`${key}-controls`}
                          menuButton={
                            <Icon
                              id="ellipses"
                              height={18}
                              width={18}
                              className=""
                            />
                          }
                          menuItems={[
                            {
                              name: 'Edit',
                              value: 'edit',
                              onClick: () => {
                                setSelectedCombination({
                                  index: idx,
                                  item: combination,
                                })
                                handleModalOpen('editVariationModal')
                              },
                            },
                            ...(usesFulfilmentCentreInventory && productDetails
                              ? [
                                  {
                                    name: 'Adjust stock',
                                    value: 'stock',
                                    onClick: () => {
                                      setStockCombination(combination)
                                      handleModalOpen('updateVariantStockModal')
                                    },
                                  },
                                ]
                              : []),
                          ]}
                        />,
                      ],
                    }
                  })}
                />
              </div>
            </>
          )}
        </>
      )}

      <AddProductVariantModal
        open={modalOpen && modal === 'addProductVariantModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
          setSelectedVariant(undefined)
        }}
        onSuccessCallback={(item) => {
          let variants: CreatedProductVariantOption[] = JSON.parse(
            JSON.stringify(values.variants ?? []),
          )

          if (selectedVariant) {
            variants = [
              ...variants.filter(
                (variant) => variant.variant?._id !== selectedVariant.item._id,
              ),
              ...item,
            ]
          } else {
            variants = [...variants, ...item]
          }
          setFieldValue('variants', variants)
          setSelectedVariant(undefined)
          handleModalClose()
        }}
        selectedVariant={selectedVariant?.item}
        productVariants={values.variants}
        loading={loadingVariants}
        variants={variants?.data.docs ?? []}
        mode={selectedVariant ? 'edit' : 'create'}
      />

      <EditVariantionCombinationModal
        open={modalOpen && modal === 'editVariationModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
          setSelectedCombination(undefined)
        }}
        onSuccessCallback={(item) => {
          const variantCombinationsCopy: VariantCombination[] = JSON.parse(
            JSON.stringify(values.variantCombinations ?? []),
          )
          if (selectedCombination) {
            variantCombinationsCopy[selectedCombination.index] = item
          }
          setFieldValue('variantCombinations', variantCombinationsCopy)
          setSelectedCombination(undefined)
          handleModalClose()
        }}
        variantCombination={selectedCombination?.item}
        usesFulfilmentCentreInventory={usesFulfilmentCentreInventory}
      />

      <UpdateItemStockModal
        open={modalOpen && modal === 'updateVariantStockModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
          setStockCombination(undefined)
        }}
        onSuccessCallback={(adjustment) => {
          handleModalClose()
          setStockCombination(undefined)
          if (adjustment) onInventoryAdjusted(adjustment)
        }}
        product={productDetails}
        fulfilmentCentres={fulfilmentCentres}
        variantCombination={stockCombination}
        type="quantity"
      />
    </div>
  )
}
